import React from 'react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { Shield, Lock, Eye, Database, UserCheck, Mail } from 'lucide-react';

export const PrivacyPolicyPage = () => {
  const sections = [
    {
      icon: Database,
      title: "Information We Collect",
      content: [
        {
          subtitle: "Personal Information",
          text: "When you use our trademark registration services or contact us through our website, we collect personal information that you voluntarily provide, including your name, email address, phone number, business name, mailing address, and trademark-related information such as the mark you wish to register, goods/services descriptions, and filing basis information."
        },
        {
          subtitle: "Automatically Collected Information",
          text: "We automatically collect certain information when you visit our website, including your IP address, browser type, operating system, referring URLs, pages viewed, time spent on pages, and other diagnostic data. We use cookies and similar tracking technologies to enhance your browsing experience and analyze website traffic patterns."
        },
        {
          subtitle: "Payment Information",
          text: "When you purchase our services, we collect payment information necessary to process your transaction. However, we do not store complete credit card information on our servers. Payment processing is handled by secure third-party payment processors who comply with PCI-DSS standards."
        }
      ]
    },
    {
      icon: Eye,
      title: "How We Use Your Information",
      content: [
        {
          subtitle: "Service Delivery",
          text: "We use your information primarily to provide trademark registration services, including conducting trademark searches, preparing and filing USPTO applications, communicating with the USPTO on your behalf, responding to office actions, monitoring your trademark status, and providing ongoing support throughout the registration process."
        },
        {
          subtitle: "Communication",
          text: "We use your contact information to send important updates about your trademark application, respond to your inquiries, provide customer support, send service-related notifications, and share relevant information about trademark law changes that may affect your registration."
        },
        {
          subtitle: "Business Operations",
          text: "Your information helps us improve our services, analyze usage patterns to enhance website functionality, maintain accurate business records, comply with legal obligations, prevent fraud and maintain security, and process payments and send invoices."
        },
        {
          subtitle: "Marketing Communications",
          text: "With your consent, we may send promotional emails about our services, trademark tips, and industry updates. You can opt out of marketing communications at any time by clicking the unsubscribe link in any email or contacting us directly."
        }
      ]
    },
    {
      icon: Lock,
      title: "Information Sharing and Disclosure",
      content: [
        {
          subtitle: "USPTO and Government Agencies",
          text: "As part of our trademark registration services, we share your information with the United States Patent and Trademark Office (USPTO) as required to file and prosecute your trademark application. Information submitted to the USPTO becomes part of the public record."
        },
        {
          subtitle: "Service Providers",
          text: "We may share your information with trusted third-party service providers who assist us in operating our business, including payment processors, email service providers, hosting providers, and analytics services. These providers are contractually obligated to maintain the confidentiality of your information."
        },
        {
          subtitle: "Legal Requirements",
          text: "We may disclose your information when required by law, court order, or government regulation, or when we believe disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to government requests."
        },
        {
          subtitle: "Business Transfers",
          text: "In the event of a merger, acquisition, reorganization, or sale of assets, your information may be transferred as part of that transaction. We will notify you via email and/or prominent notice on our website of any such change in ownership or control."
        },
        {
          subtitle: "With Your Consent",
          text: "We may share your information for any other purpose with your explicit consent or at your direction."
        }
      ]
    },
    {
      icon: Shield,
      title: "Data Security",
      content: [
        {
          subtitle: "Security Measures",
          text: "We implement industry-standard security measures to protect your personal information from unauthorized access, alteration, disclosure, or destruction. These measures include encryption of data in transit using SSL/TLS protocols, secure servers protected by firewalls, regular security audits and vulnerability assessments, restricted access to personal information on a need-to-know basis, and employee training on data protection and privacy practices."
        },
        {
          subtitle: "Data Retention",
          text: "We retain your personal information for as long as necessary to provide our services, comply with legal obligations, resolve disputes, and enforce our agreements. Trademark application files are typically retained for at least 10 years after registration or abandonment to support ongoing trademark maintenance and renewal services."
        },
        {
          subtitle: "Limitations",
          text: "While we strive to protect your personal information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security, and you transmit information to us at your own risk."
        }
      ]
    },
    {
      icon: UserCheck,
      title: "Your Privacy Rights",
      content: [
        {
          subtitle: "Access and Correction",
          text: "You have the right to access the personal information we hold about you and request corrections if it is inaccurate or incomplete. Contact us to review or update your information."
        },
        {
          subtitle: "Data Portability",
          text: "You may request a copy of your personal information in a structured, commonly used, and machine-readable format where technically feasible."
        },
        {
          subtitle: "Deletion",
          text: "You may request deletion of your personal information, subject to certain exceptions such as when we are legally required to retain the information or need it to complete services you've requested. Note that trademark application data filed with the USPTO becomes part of the permanent public record and cannot be deleted."
        },
        {
          subtitle: "Opt-Out Rights",
          text: "You can opt out of marketing communications at any time. You can also disable cookies through your browser settings, though this may affect website functionality."
        },
        {
          subtitle: "California Privacy Rights",
          text: "If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information we collect, the right to delete your information (with certain exceptions), the right to opt-out of the sale of your information (we do not sell personal information), and the right to non-discrimination for exercising your privacy rights."
        }
      ]
    },
    {
      icon: Mail,
      title: "Cookies and Tracking Technologies",
      content: [
        {
          subtitle: "What Are Cookies",
          text: "Cookies are small text files placed on your device that help us provide and improve our services. We use both session cookies (which expire when you close your browser) and persistent cookies (which remain on your device until deleted or expired)."
        },
        {
          subtitle: "Types of Cookies We Use",
          text: "Essential cookies are necessary for website functionality and cannot be disabled. Analytics cookies help us understand how visitors use our website. Preference cookies remember your settings and preferences. Marketing cookies track your browsing activity to deliver relevant advertisements (only with your consent)."
        },
        {
          subtitle: "Managing Cookies",
          text: "Most web browsers automatically accept cookies, but you can modify your browser settings to decline cookies. However, disabling cookies may prevent you from using certain features of our website."
        }
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Privacy Policy | Trademark Fashion Brand - How We Protect Your Data"
        description="Learn how Trademark Fashion Brand collects, uses, and protects your personal information. Our comprehensive privacy policy details data security measures, your rights, and our commitment to confidentiality."
        keywords="privacy policy, data protection, personal information, data security, CCPA compliance, trademark privacy, confidentiality, data rights"
        canonical="/privacy-policy"
      />
      <div className="min-h-screen bg-[#FFF7ED]">
        <Header openPopup={() => {}} />
      
      <div className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Page Header */}
            <div className="text-center mb-16">
              <h1 className="text-5xl md:text-6xl font-bold text-[#7C2D12] mb-6">
                Privacy Policy
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
                Your privacy is important to us. This Privacy Policy explains how Trademark Fashion Brand collects, uses, discloses, and safeguards your personal information.
              </p>
              <p className="text-sm text-gray-500">
                Last Updated: December 2024
              </p>
            </div>

            {/* Introduction */}
            <Card className="p-8 bg-white border-2 border-[#FED7AA] mb-12">
              <p className="text-gray-700 leading-relaxed text-lg mb-4">
                At Trademark Fashion Brand, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy describes our practices concerning the information we collect through our website, services, and communications.
              </p>
              <p className="text-gray-700 leading-relaxed text-lg">
                By using our website or services, you consent to the collection, use, and disclosure of your information as described in this Privacy Policy. If you do not agree with this policy, please do not use our services or website.
              </p>
            </Card>

            {/* Main Content Sections */}
            <div className="space-y-12">
              {sections.map((section, index) => {
                const IconComponent = section.icon;
                return (
                  <Card key={index} className="p-8 bg-white border-2 border-[#FED7AA] hover:border-[#F97316] transition-all duration-300">
                    <div className="flex items-start mb-6">
                      <div className="bg-[#F97316] p-3 rounded-lg mr-4">
                        <IconComponent className="text-white" size={28} />
                      </div>
                      <h2 className="text-3xl font-bold text-[#7C2D12] mt-1">
                        {section.title}
                      </h2>
                    </div>
                    
                    <div className="space-y-6">
                      {section.content.map((item, idx) => (
                        <div key={idx}>
                          <h3 className="text-xl font-semibold text-[#7C2D12] mb-3">
                            {item.subtitle}
                          </h3>
                          <p className="text-gray-700 leading-relaxed">
                            {item.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Contact Section */}
            <Card className="p-8 bg-gradient-to-r from-[#7C2D12] to-[#F97316] border-0 mt-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                Questions About Privacy?
              </h2>
              <p className="text-white/95 text-lg leading-relaxed mb-4">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="text-white/95 space-y-2">
                <p><strong>Email:</strong> info@trademarkfashionbrand.com</p>
                <p><strong>Phone:</strong> +1 (123) 123-4567</p>
                <p><strong>Mail:</strong> 10815 Rancho Bernardo Rd #300, San Diego, CA 92127</p>
              </div>
              <p className="text-white/90 text-sm mt-6">
                We will respond to your inquiry within 30 days. For privacy-related requests, please include "Privacy Request" in your subject line.
              </p>
            </Card>

            {/* Changes to Policy */}
            <Card className="p-8 bg-[#FFF7ED] border-2 border-[#FED7AA] mt-8">
              <h2 className="text-2xl font-bold text-[#7C2D12] mb-4">
                Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the updated policy on our website with a new "Last Updated" date. Your continued use of our services after such changes constitutes your acceptance of the updated policy. We encourage you to review this Privacy Policy periodically.
              </p>
            </Card>
          </div>
        </div>
      </div>

      <Footer openPopup={() => {}} />
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
